// Web-push registration (Jose 2026-10-13) — the browser/PWA counterpart of the native initPush. It
// registers an FCM *web* token into the SAME `pushTokens` collection (platform:'web') so broadcastPush
// and the appointment-trigger functions reach web-portal clients, not just native-app installs. This is
// what turns the ~160+ Fresha-blast web signups into a free, reachable push audience before Oct 19.
//
// SAFETY: we never auto-prompt for permission here (a denied permission is sticky and kills that user
// forever). The UI (EnableNotificationsCard) calls enableWebPush() only on an explicit tap. initWebPush()
// is the silent re-register path for users who ALREADY granted permission on a prior visit.
import { Capacitor } from '@capacitor/core';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import app, { db } from '../config/firebase';

const VAPID = process.env.REACT_APP_FIREBASE_VAPID_KEY || '';

export type WebPushState = 'unsupported' | 'no-key' | 'denied' | 'default' | 'granted';

export function webPushState(): WebPushState {
  if (typeof window === 'undefined' || Capacitor.isNativePlatform()) return 'unsupported';
  if (!('serviceWorker' in navigator) || !('Notification' in window) || !('PushManager' in window)) return 'unsupported';
  if (!VAPID) return 'no-key';
  return Notification.permission as WebPushState; // 'granted' | 'denied' | 'default'
}

async function registerToken(clientId: string): Promise<boolean> {
  const { isSupported, getMessaging, getToken, onMessage } = await import('firebase/messaging');
  if (!(await isSupported())) return false;

  // Register the background SW, passing this environment's Firebase config in the query string so the
  // one static /firebase-messaging-sw.js works for both prod and staging.
  const cfg = app.options as any;
  const qs = new URLSearchParams({
    apiKey: cfg.apiKey || '',
    projectId: cfg.projectId || '',
    messagingSenderId: cfg.messagingSenderId || '',
    appId: cfg.appId || '',
  }).toString();
  const swReg = await navigator.serviceWorker.register(`/firebase-messaging-sw.js?${qs}`);

  const messaging = getMessaging(app);
  const token = await getToken(messaging, { vapidKey: VAPID, serviceWorkerRegistration: swReg });
  if (!token) return false;

  // Resolve phone for a robust cross-link (staff-booked appts match tokens by phone).
  let phone = '';
  try {
    const snap = await getDoc(doc(db, 'clients', clientId));
    phone = (snap.exists() && ((snap.data() as any).phone || '')) || '';
  } catch { /* phone optional */ }

  await setDoc(doc(db, 'pushTokens', token), {
    clientId, phone, token, platform: 'web', app: 'customer', updatedAt: serverTimestamp(),
  }, { merge: true });

  // Turning on notifications = the client's OPT-IN to be messaged (push + in-app) — the no-phone-number
  // channel (some clients hesitated on the SMS number). Record it on the account so broadcasts/targeting
  // treat them as reachable + opted-in. Stored under `preferences` (the only customer-writable map in the
  // security rules). SMS-text consent stays separate (TCPA — this button says "no texts"). Fail-soft:
  // the push token is the primary success. Queryable via preferences.messagingOptIn. (Jose 2026-10-15)
  try {
    await setDoc(doc(db, 'clients', clientId), {
      preferences: {
        messagingOptIn: true,
        messagingOptInAt: serverTimestamp(),
        messagingOptInSource: 'notification-card',
      },
    }, { merge: true });
  } catch { /* opt-in flag is best-effort; token already saved */ }

  // Foreground messages (tab open) — the SW only fires in the background, so show it ourselves.
  onMessage(messaging, (payload) => {
    const n = (payload && (payload as any).notification) || {};
    if (Notification.permission === 'granted' && n.title) {
      new Notification(n.title, { body: n.body || '', icon: '/logo192.png' });
    }
  });
  return true;
}

/** Explicit opt-in — call ONLY from a user gesture (button tap). Prompts for permission if needed.
 *  Returns true if a token was registered. */
export async function enableWebPush(clientId: string): Promise<boolean> {
  if (!clientId) return false;
  const st = webPushState();
  if (st === 'unsupported' || st === 'no-key' || st === 'denied') return false;
  try {
    if (Notification.permission !== 'granted') {
      const p = await Notification.requestPermission();
      if (p !== 'granted') return false;
    }
    return await registerToken(clientId);
  } catch (e) { console.warn('[webpush] enable failed', e); return false; }
}

/** Silent re-register for users who ALREADY granted permission previously. No prompt. Safe to call on
 *  every login so tokens stay fresh. No-ops on native (native uses initPush) and when permission isn't
 *  already granted. */
let started = false;
export async function initWebPush(clientId: string): Promise<void> {
  if (!clientId || started) return;
  if (webPushState() !== 'granted') return;
  started = true;
  try { await registerToken(clientId); }
  catch (e) { started = false; console.warn('[webpush] init failed', e); }
}
